import Section from "../../components/Landing/Section";
import Objectives from "../../components/Landing/Objectives";
import FinancialIncentives from "../../components/Landing/FinancialIncentives";
import Categorization from "../../components/Landing/Categorization";
import Button from "../../components/common/Button";
import HomeLayout from "./HomeLayout";
import { INVEST_UK_WEBSITE_URL, NIVESH_WEBSITE_URL } from "../../config/apiConfig";
import Banner from "../../components/Landing/Banner";
import {
  prayatanYojanaObjectives,
  yojanaBannerItems,
  financialIncentivesData,
  prayatanYojanaSections,
  guidelinesDataPrayatanYojna,
} from "../../config/homePageUtils";
import { useNavigate } from "react-router-dom";
import ApplicationProcess from "../../components/Landing/ApplicationProcess";
import { useState } from "react";
import YojanaSections from "../../components/Landing/YojanaSections";
import { useLanguage } from "../../components/LanguageProvider/LanguageProvider";

const PrayatanYojna = () => {
  const { image, title, description, overview } =
    yojanaBannerItems.prayatanYojana;
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState("overview");
  const { language } = useLanguage();

  function obfuscateEmail(email) {
    return email.replace(/@/g, " [at] ").replace(/\./g, " [dot] ");
  }

  return (
    <HomeLayout>
      <Banner
        image={image}
        title={title}
        description={description}
        buttonUrl="/login"
        isParyatanYojana={true}
        language={language}
      />
      <YojanaSections
        options={prayatanYojanaSections}
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        language={language}
      >
        {activeSection === "overview" && (
          <Section
            id="overview"
            title={language ? "संक्षिप्त विवरण" : "Overview"}
            openByDefault={true}
          >
            <p>{language ? overview.hi : overview.en}</p>
          </Section>
        )}
        {activeSection === "investment" && (
          <Section
            id="investment"
            title={language ? "निवेश सीमा" : "Investment Limits"}
          >
            <ul className="list-disc pl-6 space-y-2">
              <li>
                {language
                  ? "न्यूनतम निवेश: ₹1 करोड़।"
                  : "Minimum investment: ₹1 crore."}
              </li>
              <li>
                {language
                  ? "अधिकतम निवेश: ₹5 करोड़।"
                  : "Maximum investment: ₹5 crores."}
              </li>
              <li>
                {language
                  ? "₹1 करोड़ से अधिक निवेश पर श्रेणी के अनुसार वित्तीय प्रोत्साहन प्रदान किए जाएंगे।"
                  : "For investments exceeding ₹1 crore, financial incentives will be provided based on the category."}
              </li>
            </ul>
          </Section>
        )}
        {activeSection === "objectives" && (
          <Objectives data={prayatanYojanaObjectives} language={language} />
        )}
        {activeSection === "eligibility" && (
          <Section id="categories" title={language ? "योग्यता" : "Eligibility"}>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                {language
                  ? "केवल उत्तराखण्ड के स्थायी निवासी/उद्यमी ही पात्र होंगे।"
                  : "Only permanent residents/entrepreneurs of Uttarakhand are eligible."}
              </li>
              <li>
                {language
                  ? "केवल उत्तराखण्ड के स्थायी निवासी/उद्यमी ही पात्र होंगे।"
                  : "The investment range should be between ₹1 crore and ₹5 crores."}
              </li>
              <li>
                {language
                  ? "केवल चिन्हित पर्यटन गतिविधियों/परियोजनाओं में निवेश करने वाले उद्यमी पात्र होंगे।"
                  : "Only entrepreneurs investing in identified tourism activities or projects are eligible."}
              </li>
              <li>
                {language
                  ? "इकाई की स्थापना हेतु भूमि का पूर्ण स्वामित्व अथवा वैध लीज होना अनिवार्य है।"
                  : "Complete ownership or lease of land is required for establishing the unit."}
              </li>
              <li>
                {language
                  ? "निर्धारित समयसीमा के भीतर सिंगल विंडो क्लीयरेंस सिस्टम के माध्यम से इन-प्रिंसिपल अनुमोदन(CAF) प्राप्त करना अनिवार्य होगा।"
                  : "In principle approval (CAF) through SWCS is compulsory within prescribed timeline."}
              </li>
            </ul>
          </Section>
        )}
        {activeSection === "incentives" && (
          <FinancialIncentives
            data={financialIncentivesData}
            language={language}
          />
        )}
        {activeSection === "categorization" && (
          <Categorization language={language} />
        )}
        {activeSection === "application" && (
          <Section
            id="application-process-section"
            title={
              language
                ? "आवेदन प्रक्रिया एवं संपर्क जानकारी"
                : "Application Process and Contact Information"
            }
          >
            <div className="">
              <h5 className="font-semibold mb-3"></h5>
              <ul className="list-unstyled space-y-3">
                <li className="d-flex align-items-start">
                  <span className="text-[#018f9b] me-2 mt-1">●</span>
                  <span>
                    {language
                      ? "निर्माण कार्य प्रारम्भ करने से पूर्व राज्य सिंगल विण्डो पोर्टल से इन-प्रिंसिपल अनुमोदन(CAF Approval) प्राप्त करना अनिवार्य है। (पोर्टल लिंक:"
                      : "Prior to construction, it is mandatory to obtain In principle Approval (CAF Approval) from the State Single Window Portal at – "}
                    <Button
                      className="text-[#018f9b]"
                      onClick={() =>
                        window.open(
                          INVEST_UK_WEBSITE_URL,
                          "_blank",
                          "noopener,noreferrer",
                        )
                      }
                    >
                      {INVEST_UK_WEBSITE_URL}
                    </Button>{`${language ? ")" : " "}`}
                  </span>
                </li>
                <li className="d-flex align-items-start">
                  <span className="text-[#018f9b] me-2 mt-1">●</span>
                  <span>
                    {language
                      ? "निर्माण/क्रियान्वयन कार्य, इन-प्रिंसिपल अनुमोदन की तिथि से अधिकतम तीन वर्ष की अवधि के भीतर, निर्धारित नियमों/प्रावधानों के अनुसार पूर्ण किया जाना आवश्यक है।"
                      : "Construction/implementation must be completed within a maximum period of three years from the date of in-principle approval, in accordance with the prescribed rules/provisions."}
                  </span>
                </li>
                <li className="d-flex align-items-start">
                  <span className="text-[#018f9b] me-2 mt-1">●</span>
                  <span>
                    {language
                      ? "पर्यटन विभाग के निवेश पोर्टल पर पंजीकरण ("
                      : "Registration on the Uttarakhand Tourism Investment Portal ("}
                    <Button
                      className="text-[#018f9b]"
                      onClick={() =>
                        window.open(
                          NIVESH_WEBSITE_URL,
                          "_blank",
                          "noopener,noreferrer",
                        )
                      }
                    >
                      {NIVESH_WEBSITE_URL}
                    </Button>{`${language ? ") कराना आवश्यक।" : ") is required."}`}
                  </span>
                </li>
                <li className="d-flex align-items-start">
                  <span className="text-[#018f9b] me-2 mt-1">●</span>
                  <span>
                    {language
                      ? "योजना के अंतर्गत अनुदान (सब्सिडी) केवल कार्य पूर्ण होने एवं व्यावसायिक संचालन प्रारम्भ होने के उपरांत ही प्रदान किया जाएगा।"
                      : "Grant (subsidy) will be provided only after completion of the scheme work and commencement of commercial operations."}
                  </span>
                </li>
              </ul>
            </div>
            <ApplicationProcess language={language} />
          </Section>
        )}
        {activeSection === "guidelines" && (
          <Section
            id="detailed-scheme-section"
            title={
              language
                ? "विस्तृत योजना जानकारी एवं दिशा-निर्देश"
                : "Detailed Scheme Information and Guidelines"
            }
            spaceY4Needed={false}
          >
            <div>
              {/* Timeline Table */}
              <div className="mb-6">
                <h3 className="text-[#018f9b] font-semibold mb-2">
                  {language ? "समयसीमा" : "Timeline"}
                </h3>

                <div className="overflow-x-auto">
                  <table className="w-full border border-gray-300 text-sm">
                    <thead className="bg-btn text-white">
                      <tr>
                        <th className="border p-2 text-white">
                          {language ? "विवरण" : "Description"}
                        </th>
                        <th className="border p-2 text-white">
                          {language ? "समयसीमा" : "Timeline"}
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {guidelinesDataPrayatanYojna.timeline.map((row, i) => (
                        <tr key={i} className="bg-white">
                          <td className="border p-3">
                            {language ? row.desc.hi : row.desc.en}
                          </td>
                          <td className="border p-3">
                            {language ? row.time.hi : row.time.en}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Downloads Table */}
              <div className="mb-6">
                <h3 className="text-[#018f9b] font-semibold mb-2">
                  {language ? "डाउनलोड" : "Downloads"}
                </h3>

                <div className="overflow-x-auto">
                  <table className="w-full border border-gray-300 text-sm">
                    <thead className="bg-btn text-white">
                      <tr>
                        <th className="border p-2 text-white">
                          {language
                            ? "शासनादेश/गाइड लाइन्स"
                            : "Government Orders / Guidelines"}
                        </th>
                        <th className="border p-2 text-white">
                          {language
                            ? "अनुदान हेतु वांछित अभिलेख"
                            : "Documents required for Subsidy Claim"}
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr className="bg-white align-top">
                        {/* LEFT COLUMN */}
                        <td className="border p-3">
                          {guidelinesDataPrayatanYojna.downloads.govt.map(
                            (item, i) => (
                              <div key={i} className="mb-2">
                                <div
                                  key={i}
                                  onClick={() =>
                                    window.open(
                                      item.file,
                                      "_blank",
                                      "noopener,noreferrer",
                                    )
                                  }
                                  className="mb-2 cursor-pointer text-blue-600 hover:underline"
                                >
                                  <span className="text-[#018f9b] mr-2 mt-1">
                                    ●
                                  </span>{" "}
                                  {language ? item.label.hi : item.label.en}
                                </div>
                              </div>
                            ),
                          )}
                        </td>

                        {/* RIGHT COLUMN */}
                        <td className="border p-3">
                          {guidelinesDataPrayatanYojna.downloads.docs.map(
                            (item, i) => (
                              <div key={i} className="mb-2">
                                <div
                                  key={i}
                                  onClick={() =>
                                    window.open(
                                      item.file,
                                      "_blank",
                                      "noopener,noreferrer",
                                    )
                                  }
                                  className="mb-2 cursor-pointer text-blue-600 hover:underline"
                                >
                                  <span className="text-[#018f9b] mr-2 mt-1">
                                    ●
                                  </span>{" "}
                                  {language ? item.label.hi : item.label.en}
                                </div>
                              </div>
                            ),
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <ul className="list-unstyled space-y-4">
                <li className="bg-white border border-gray-200 shadow-sm rounded-md p-4 flex items-start">
                  <span className="text-[#018f9b] mr-2 mt-1">●</span>
                  <span>
                    {language
                      ? "समर्पित पर्यटन निवेश सहायता केंद्र"
                      : "Dedicated Tourism Investment Support Center"}
                  </span>
                </li>

                <li className="bg-white border border-gray-200 shadow-sm rounded-md p-4 flex items-start">
                  <span className="text-[#018f9b] mr-2 mt-1">●</span>
                  <span>
                    {language
                      ? "TIFC संपर्क नंबर: 0135-3515001 / 3515003 / 3510143"
                      : "TIFC contact no. 0135-3515001 / 3515003 / 3510143"}
                  </span>
                </li>

                <li className="bg-white border border-gray-200 shadow-sm rounded-md p-4 flex items-start">
                  <span className="text-[#018f9b] mr-2 mt-1">●</span>
                  <span>
                    {language
                      ? "ईमेल: tifcuttarakhandtourism [at] gmail [dot] com"
                      : "Email: tifcuttarakhandtourism [at] gmail [dot] com"}
                  </span>
                </li>
              </ul>
            </div>
          </Section>
        )}
      </YojanaSections>
      <section className="px-4 py-10 md:px-20 lg:px-32">
        <div className="max-w-7xl mx-auto px-0">
          <div className="inline-flex items-center bg-[#018f9b] text-white px-6 py-3 rounded-full font-semibold hover:opacity-90 transition">
            <Button
              className="ml-2 text-lg text-white"
              onClick={() => {
                navigate("/login");
              }}
            >
              {language ? "अभी अप्लाई करें" : "Apply Now/ Login"} →
            </Button>
          </div>
        </div>
      </section>{" "}
    </HomeLayout>
  );
};

export default PrayatanYojna;
